/**
 * "Learn How to Play Euchre": an interactive tutorial on the real table, played entirely in this browser.
 *
 * How it works:
 * - The real game engine (@euchre/core) runs here, with pre-arranged deals (createGame({ deck })) so each
 *   lesson always gets the hand it needs. Bots use the same brains as on the server, plus a few scripted moves
 *   (`plan`) where a lesson depends on them.
 * - The table is the normal table screen: the tutorial hands it room snapshots through receiveLocal(), and the
 *   table's taps come back here through setLocalGame() instead of going to the server.
 * - A coach box above the prompt walks through the lessons. A step either waits for "Next", waits for one
 *   particular move (anything else gets a friendly tip instead), or lets the hand play on until something happens.
 * - Nothing is sent to the server, so it costs nothing to run and nobody can join.
 */

import {
  type Card,
  type ClientMessage,
  type GameAction,
  type GameState,
  type PlayerView,
  type RoomSnapshot,
  SUIT_NAMES,
  type Seat,
  applyAction,
  chooseBotAction,
  createGame,
  ledSuitOf,
  stackedDeck,
  startNextHand,
  viewFor,
  waitingOn,
} from '@euchre/core';
import { h } from '../dom.js';
import { app, receiveLocal, rerender, setLocalGame } from '../state.js';
import type { DealerTalk } from './voice.js';
import { allowRetry } from './table.js';

/** The tutorial table's code. It never reaches the server, and the address bar doesn't change for it. */
export const TUTORIAL_CODE = 'LEARN';

const ME: Seat = 0;
const LOU: Seat = 1;
const PAT: Seat = 2;
const RAY: Seat = 3;
/** Lou sits on your Left, Ray on your Right, and Pat, your Partner, across from you. */
const BOT_NAMES: Record<number, string> = { 1: 'Lou', 2: 'Pat', 3: 'Ray' };

type Hands = [Card[], Card[], Card[], Card[]];

interface Step {
  /** What the coach says. */
  say: (g: GameState) => string;
  /** Waits for the player to tap Next (the game is paused meanwhile). */
  next?: boolean;
  /** Moves on by itself as soon as this is true. */
  until?: (g: GameState) => boolean;
  /** Which of the player's moves this step accepts: true, or a tip explaining what to do instead. */
  allow?: (a: GameAction, g: GameState) => true | string;
  /** What to highlight on the table. */
  point?: (root: HTMLElement) => Element[];
  /** The bots may take their turns during this step. */
  bots?: boolean;
}

interface Lesson {
  title: string;
  /** Deals this lesson's hand (given the game so far). */
  start?: (prev: GameState | null) => GameState;
  /** Scripted bot moves for this lesson; null lets the bot decide. */
  plan?: (g: GameState, seat: Seat) => GameAction | null;
  steps: Step[];
}

// ---------------------------------------------------------------------------
// The two lesson hands
// ---------------------------------------------------------------------------

/** Hand 1: Ray deals, so you bid and lead first. You hold both red jacks; the 9♥ is turned up. */
function firstHand(): GameState {
  const hands: Hands = [
    ['JH', 'JD', 'AH', 'KS', 'AC'], // you
    ['10S', 'QS', '9C', '10D', 'QD'], // Lou
    ['KH', 'QH', 'AS', 'KC', '9D'], // Pat
    ['10H', '9S', 'JS', 'QC', 'KD'], // Ray (dealer)
  ];
  return createGame({ seed: 'learn-1', firstDealer: RAY, deck: stackedDeck(RAY, hands, ['9H', '10C', 'JC', 'AD']) });
}

/** Hand 2: Pat deals, so Ray bids and leads first. Nobody wants the 9♣; you name Spades in round two. */
function secondHand(prev: GameState | null): GameState {
  const hands: Hands = [
    ['JS', 'AS', 'KS', '10D', 'QH'], // you
    ['JC', '9D', 'QD', 'AH', '10C'], // Lou
    ['KH', 'JH', 'JD', 'QS', '10S'], // Pat (dealer)
    ['AD', 'KD', '9H', '10H', 'QC'], // Ray
  ];
  const g = createGame({ seed: 'learn-2', firstDealer: PAT, deck: stackedDeck(PAT, hands, ['9C', 'KC', 'AC', '9S']) });
  if (prev) {
    // Carry the score on, and count this as the next hand (so the deal animation plays).
    g.scores = [...prev.scores];
    g.history = prev.history;
    g.handNumber = prev.handNumber + 1;
    g.dealCount = prev.dealCount + 1;
    g.hand.dealNumber = g.dealCount;
    g.version = prev.version + 1;
  }
  return g;
}

// ---------------------------------------------------------------------------
// Helpers for the steps
// ---------------------------------------------------------------------------

const card = (c: Card) => (root: HTMLElement) => [...root.querySelectorAll(`.hand [data-card="${c}"]`)];
const button = (label: RegExp) => (root: HTMLElement) =>
  [...root.querySelectorAll<HTMLButtonElement>('.prompt-actions button')].filter((b) => label.test(b.textContent ?? '') || label.test(b.title));
const onlyPlay = (c: Card, tip: string) => (a: GameAction) => (a.type === 'play' && a.card === c ? true : tip);
const myTurn = (g: GameState) => waitingOn(g) === ME;
const handOver = (g: GameState) => g.phase === 'hand_complete' || g.phase === 'game_over';
const tricksDone = (n: number) => (g: GameState) => g.hand.tricks.length >= n && (myTurn(g) || handOver(g) || g.hand.currentTrick.length === 0);
const nameOf = (s: Seat) => (s === ME ? 'You' : BOT_NAMES[s]!);

function resultTalk(g: GameState): string {
  const r = g.hand.result;
  if (!r) return '';
  const we = r.makerTeam === 0;
  const who = we ? 'Your team' : 'Lou and Ray';
  if (r.outcome === 'march') return `${who} took all 5 tricks. That's a march, worth 2 points.`;
  if (r.outcome === 'euchre') {
    return we
      ? `Your team called trump but took only ${r.makerTricks} trick${r.makerTricks === 1 ? '' : 's'}, so you were euchred: Lou and Ray score 2 points.`
      : `Lou and Ray called trump but your team took 3 or more tricks. You euchred them: 2 points for your team!`;
  }
  return `${who} called trump and took ${r.makerTricks} tricks, so ${we ? 'your team scores' : 'they score'} 1 point.`;
}

// ---------------------------------------------------------------------------
// The lessons
// ---------------------------------------------------------------------------

const LESSONS: Lesson[] = [
  {
    title: 'The basics',
    start: () => firstHand(),
    steps: [
      {
        next: true,
        say: () =>
          'Welcome to Euchre! It’s played by four people in two teams. Pat, across from you, is your partner. Lou (on your left) and Ray (on your right) are the other team. The first team to 10 points wins.',
      },
      {
        next: true,
        say: () =>
          'Euchre uses just 24 cards: the 9, 10, Jack, Queen, King and Ace of each suit. Everyone gets 5 cards. The other 4 go in the middle as the kitty, and the top one is turned face up.',
        point: (root) => [...root.querySelectorAll('.kitty-stack')],
      },
      {
        next: true,
        say: () =>
          'Each hand has 5 tricks. In a trick, everyone plays one card and the highest card wins. The team that picks trump needs at least 3 of the 5 tricks.',
      },
      {
        next: true,
        say: () => 'Trump is the suit that beats every other suit. The turned-up card is the 9♥, so this time the choice is whether Hearts will be trump.',
        point: (root) => [...root.querySelectorAll('.kitty-stack')],
      },
      {
        next: true,
        say: () =>
          'The two highest trumps are Jacks, called the bowers. With Hearts as trump, the J♥ is the highest card in the deck (the right bower). Next comes the other red Jack, the J♦ (the left bower), which counts as a Heart. Then A♥, K♥, Q♥, 10♥, 9♥.',
        point: (root) => [...card('JH')(root), ...card('JD')(root)],
      },
    ],
  },
  {
    title: 'Bidding',
    steps: [
      {
        next: true,
        say: () =>
          'Bidding starts with the player on the dealer’s left. Ray is dealing, so that’s you. You can order up the turned card, which makes its suit trump (and the dealer picks that card up), or pass.',
      },
      {
        say: () => 'You hold both bowers and the A♥: three of the top Hearts. That’s a great Hearts hand. Tap “Order it up”.',
        allow: (a) =>
          a.type === 'order_up' ? (a.alone ? 'Going alone comes later. Untick “Go alone” and order it up with your partner.' : true) : 'With three top Hearts, order it up!',
        point: button(/Order it up/),
        until: (g) => g.phase !== 'bidding1',
      },
      {
        bots: true,
        say: () => 'Hearts are trump! Ray, the dealer, picks up the 9♥ and throws away one card. Your team called trump, so you need 3 tricks.',
        until: (g) => g.phase === 'playing' && myTurn(g),
      },
    ],
  },
  {
    title: 'Playing tricks',
    steps: [
      {
        bots: true,
        say: () => 'The player on the dealer’s left leads the first trick: you. Lead your highest trump, the right bower (J♥), to pull out everyone else’s trumps.',
        allow: onlyPlay('JH', 'Start with the J♥, the highest card in the deck.'),
        point: card('JH'),
        until: tricksDone(1),
      },
      {
        next: true,
        say: (g) => {
          const t = g.hand.tricks[0];
          return `${t ? nameOf(t.winner) : 'You'} won the trick. Everyone must follow the suit that was led if they can, so Pat and Ray had to play Hearts. Lou had none, so Lou could play anything. The winner leads the next trick.`;
        },
      },
      {
        bots: true,
        say: () => 'Now lead the left bower, the J♦. While Hearts are trump it counts as a Heart, so the others still have to follow with Hearts.',
        allow: onlyPlay('JD', 'Lead the J♦ (the left bower) next.'),
        point: card('JD'),
        until: tricksDone(2),
      },
      {
        bots: true,
        say: () => 'You’re in charge now. Play out the rest of the hand. Tip: lead your A♥ to clear the last trump, then cash your other Aces.',
        until: handOver,
      },
    ],
  },
  {
    title: 'Scoring',
    steps: [
      {
        next: true,
        say: (g) =>
          `${resultTalk(g)} Scoring: the team that called trump gets 1 point for 3 or 4 tricks and 2 points for all 5 (a march). If they get fewer than 3, they’re euchred and the other team gets 2.`,
      },
    ],
  },
  {
    title: 'Round two and following suit',
    start: (prev) => secondHand(prev),
    plan: (g, seat) => {
      if (g.phase === 'bidding1' && seat !== ME) return { type: 'pass' };
      if (g.phase === 'bidding2' && seat === RAY) return { type: 'pass' };
      if (g.phase === 'playing' && seat === RAY && g.hand.tricks.length === 0 && g.hand.currentTrick.length === 0) return { type: 'play', card: 'AD' };
      return null;
    },
    steps: [
      {
        next: true,
        say: () => 'A new hand. The deal moves one seat to the left, so Pat is dealing. That makes Ray first to bid. The 9♣ is turned up.',
      },
      {
        bots: true,
        say: () => 'Ray decides first…',
        until: myTurn,
      },
      {
        say: () => 'Ray passed. You have no Clubs at all, so Clubs would be a bad trump for you. Tap “Pass”.',
        allow: (a) => (a.type === 'pass' ? true : 'With no Clubs, let this one go. Tap “Pass”.'),
        point: button(/^Pass$/),
        until: (g) => !(g.phase === 'bidding1' && myTurn(g)),
      },
      {
        bots: true,
        say: () => 'Lou and Pat pass too…',
        until: (g) => g.phase === 'bidding2' && myTurn(g),
      },
      {
        say: () =>
          'Nobody ordered up the 9♣, so it’s turned face down and there’s a second round. Now anyone can name a different suit as trump, or pass. Ray passed again. You hold the J♠, A♠ and K♠, so name Spades.',
        allow: (a) =>
          a.type === 'call_trump' && a.suit === 'S'
            ? a.alone
              ? 'Untick “Go alone” for now, then name Spades.'
              : true
            : a.type === 'call_trump'
              ? 'You have three top Spades. Name Spades instead.'
              : 'Name Spades: you have the J♠, A♠ and K♠.',
        point: button(/Call Spades/),
        until: (g) => g.phase !== 'bidding2',
      },
      {
        bots: true,
        say: () => 'Spades are trump. Now the J♠ is the right bower and the J♣ (the other black Jack) is the left bower. Ray is on the dealer’s left, so Ray leads first…',
        until: (g) => g.phase === 'playing' && myTurn(g),
      },
      {
        bots: true,
        say: () =>
          'Ray led the A♦. You have a Diamond (the 10♦), so you must follow suit and play it, even though it can’t win. The cards you can’t play are greyed out.',
        allow: onlyPlay('10D', 'You have to follow Diamonds with your 10♦.'),
        point: card('10D'),
        until: tricksDone(1),
      },
      {
        bots: true,
        say: () =>
          'When you can’t follow suit, you can play a trump to win the trick, or throw away a low card you don’t need. Play out the rest of the hand.',
        until: handOver,
      },
      {
        next: true,
        say: (g) =>
          `${resultTalk(g)} One more thing: with a really strong hand, you can tick “Go alone” when you bid. Your partner sits out, and if you take all 5 tricks by yourself your team scores 4 points.`,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Tips during free play (after the lessons)
// ---------------------------------------------------------------------------

function freeTip(g: GameState): string {
  const v: PlayerView = viewFor(g, ME);
  if (g.phase === 'game_over') return g.winner === 0 ? 'You won the game! Nicely played.' : 'Lou and Ray won this one. Want to go again?';
  if (g.phase === 'hand_complete') return `${resultTalk(g)} The next hand is coming up…`;
  if (v.turn !== ME) return `Watch what ${nameOf(v.turn ?? RAY)} does…`;
  const up = SUIT_NAMES[g.hand.upCard.slice(-1) as keyof typeof SUIT_NAMES];
  switch (g.phase) {
    case 'bidding1':
      return `Tip: order up if ${up} would give you about 3 trumps (counting both Jacks of that colour). Otherwise pass.`;
    case 'bidding2':
      return 'Tip: name a suit you hold three or more of (counting its bowers), or pass.';
    case 'discard':
      return `Tip: throw away your weakest card that isn’t trump, ideally your only card of a suit.`;
    case 'playing': {
      if (g.hand.currentTrick.length === 0) return 'Tip: lead a strong card. With lots of trump, lead trump; otherwise lead an Ace.';
      const led = ledSuitOf(g.hand.currentTrick, g.hand.trump!);
      const playable = v.legalActions.filter((a) => a.type === 'play').length;
      if (led && playable < v.myHand.length) return `Tip: ${SUIT_NAMES[led]} were led, and you have some, so you must play one.`;
      return 'Tip: you can’t follow suit. Trump it to win the trick, or throw away a low card.';
    }
    default:
      return 'Your turn.';
  }
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

let game: GameState | null = null;
let lessonIdx = 0;
let stepIdx = 0;
let tip: string | null = null;
let freePlay = false;
let timer = 0;
/** The player's own dealer voice, put back when they leave (the tutorial uses Plain wording to keep things clear). */
let savedTalk: DealerTalk | null = null;

export function tutorialActive(): boolean {
  return game !== null;
}

/** Start the tutorial from the home screen. */
export function startTutorial(): void {
  window.clearTimeout(timer);
  lessonIdx = 0;
  stepIdx = 0;
  tip = null;
  freePlay = false;
  savedTalk = app.ui.dealerTalk;
  app.ui.dealerTalk = 'plain';
  app.ui.drawerOpen = false;
  app.ui.menuOpen = false;
  app.ui.alone = false;
  game = LESSONS[0]!.start!(null);
  setLocalGame(handle);
  settle();
}

export function exitTutorial(): void {
  window.clearTimeout(timer);
  game = null;
  setLocalGame(null);
  if (savedTalk) app.ui.dealerTalk = savedTalk;
  savedTalk = null;
  delete document.body.dataset.coachWait;
  receiveLocal({ type: 'left_room' });
}

function step(): Step | null {
  if (freePlay) return null;
  return LESSONS[lessonIdx]?.steps[stepIdx] ?? null;
}

function lessonsDone(): boolean {
  return !freePlay && lessonIdx >= LESSONS.length;
}

function advance(): void {
  tip = null;
  stepIdx += 1;
  const lesson = LESSONS[lessonIdx];
  if (lesson && stepIdx >= lesson.steps.length) {
    lessonIdx += 1;
    stepIdx = 0;
    const nextLesson = LESSONS[lessonIdx];
    if (nextLesson?.start) game = nextLesson.start(game);
  }
}

/** After every change: move past finished steps, show the table, and let the bots move if it's their turn. */
function settle(): void {
  for (let guard = 0; guard < 20 && game; guard++) {
    const s = step();
    if (!s || s.next || !s.until || !s.until(game)) break;
    advance();
  }
  publish();
  schedule();
}

function publish(): void {
  if (game) receiveLocal({ type: 'room', room: snapshot(game) });
}

function snapshot(g: GameState): RoomSnapshot {
  const me = app.playerId || 'you';
  return {
    code: TUTORIAL_CODE,
    hostId: me,
    status: g.phase === 'game_over' ? 'finished' : 'playing',
    rules: g.rules,
    seats: ([0, 1, 2, 3] as Seat[]).map((seat) => ({
      seat,
      occupant:
        seat === ME
          ? { kind: 'human', playerId: me, name: app.name || 'You', connected: true, ready: true, botTakeoverAt: null, botControlled: false }
          : { kind: 'bot', name: BOT_NAMES[seat]! },
    })),
    spectators: [],
    you: { playerId: me, seat: ME, isHost: true, spectator: false },
    game: viewFor(g, ME),
    chat: [],
    serverTime: Date.now(),
    gamesWon: [0, 0],
  };
}

/** Let a bot take its turn after a short pause, if this step allows it. */
function schedule(): void {
  window.clearTimeout(timer);
  const g = game;
  if (!g) return;
  if (g.phase === 'hand_complete') {
    if (freePlay) timer = window.setTimeout(() => ((game = startNextHand(g)), settle()), 4200);
    return;
  }
  const seat = waitingOn(g);
  if (seat === null || seat === ME) return;
  if (!freePlay && !step()?.bots) return;
  const bidding = g.phase === 'bidding1' || g.phase === 'bidding2';
  // After a trick, the table keeps it on show for 2 seconds; wait for that, like the real server does.
  const afterTrick = g.phase === 'playing' && g.hand.currentTrick.length === 0 && g.hand.tricks.length > 0;
  const delay = afterTrick ? 2400 : bidding ? 1400 : 1000;
  timer = window.setTimeout(() => {
    if (game !== g) return;
    const plan = freePlay ? null : LESSONS[lessonIdx]?.plan?.(g, seat);
    const action = plan ?? chooseBotAction(viewFor(g, seat));
    game = applyAction(g, seat, action);
    settle();
  }, delay);
}

/** The table's messages while the tutorial is on. Returns false for what should still go to the server. */
function handle(msg: ClientMessage): boolean {
  switch (msg.type) {
    case 'game_action':
      playerMove(msg.action);
      return true;
    case 'leave_room':
    case 'stand':
      exitTutorial();
      return true;
    case 'new_game':
      startTutorial();
      return true;
    case 'feedback':
    case 'set_name':
    case 'ping':
      return false;
    default:
      return true; // chat, emotes, seating: nothing to do at a practice table
  }
}

function playerMove(a: GameAction): void {
  const g = game;
  if (!g) return;
  const s = step();
  const refuse = (why: string) => {
    tip = why;
    allowRetry();
    rerender();
  };
  if (s?.next) return refuse('Tap “Next” in the coach box first.');
  if (s?.allow) {
    const ok = s.allow(a, g);
    if (ok !== true) return refuse(ok);
  }
  try {
    game = applyAction(g, ME, a);
  } catch {
    return refuse('That move isn’t allowed right now.');
  }
  tip = null;
  settle();
}

function nextPressed(): void {
  advance();
  settle();
}

function keepPlaying(): void {
  freePlay = true;
  tip = null;
  if (game && game.phase === 'hand_complete') game = startNextHand(game);
  settle();
}

// ---------------------------------------------------------------------------
// The coach box (main.ts puts it above the prompt on the table screen)
// ---------------------------------------------------------------------------

export function coachPanel(table: HTMLElement): HTMLElement | null {
  const g = game;
  if (!g) return null;
  const s = step();
  for (const el of table.querySelectorAll('.coach-point')) el.classList.remove('coach-point');
  if (s?.point) for (const el of s.point(table)) el.classList.add('coach-point');
  // While the coach is explaining, the bid buttons stay out of the way.
  if (s?.next) document.body.dataset.coachWait = 'on';
  else delete document.body.dataset.coachWait;

  const exit = h('button', { class: 'btn small quiet coach-exit', type: 'button', text: 'Exit tutorial', onclick: exitTutorial });
  let heading: string;
  let text: string;
  let actions: (HTMLElement | null)[] = [];
  if (freePlay) {
    heading = 'Practice game';
    text = freeTip(g);
    if (g.phase === 'game_over') actions = [h('button', { class: 'btn primary', type: 'button', text: 'Start the tutorial again', onclick: startTutorial })];
  } else if (lessonsDone()) {
    heading = 'You’re ready!';
    text = 'That’s Euchre! Keep playing this game against the bots with tips turned on, or head back to the start screen and play for real.';
    actions = [
      h('button', { class: 'btn primary', type: 'button', text: 'Keep playing with tips', onclick: keepPlaying }),
      h('button', { class: 'btn', type: 'button', text: 'Back to the start', onclick: exitTutorial }),
    ];
  } else {
    const lesson = LESSONS[lessonIdx]!;
    heading = `Lesson ${lessonIdx + 1} of ${LESSONS.length}: ${lesson.title}`;
    text = s ? s.say(g) : '';
    if (s?.next) actions = [h('button', { class: 'btn primary', type: 'button', text: 'Next', onclick: nextPressed })];
  }
  return h(
    'section',
    { class: 'coach', attrs: { 'aria-live': 'polite', 'aria-label': 'Tutorial coach' } },
    h('div', { class: 'coach-head' }, h('span', { class: 'coach-title', text: heading }), exit),
    h('p', { class: 'coach-text', text }),
    tip ? h('p', { class: 'coach-tip', text: tip }) : null,
    actions.length ? h('div', { class: 'coach-actions' }, actions) : null,
  );
}
